import "./breadcrumb.css"
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useStore } from "../../stores/root-store-context";
import { useEffect, useRef, useState } from "react";

// Add TypeScript declarations
declare global {
  interface Window {
    particlesJS: (id: string, config: any) => void;
    pJSDom?: Array<{
      pJS: {
        canvas: {
          el: HTMLCanvasElement;
        }
      }
    }>;
  }
}

interface BreadcrumbItem {
  label: string;
  path: string;
}

interface BreadcrumbProps {
  breadcrumbItems?: BreadcrumbItem[];
  breadcrumbTitle?: string;
}

export default function Breadcrumb({ breadcrumbItems, breadcrumbTitle }: BreadcrumbProps) {
    const { t } = useTranslation();
    const { applicationStore } = useStore();
    const tenant = applicationStore.company?.Tenant || '';
    const particlesRef = useRef<HTMLDivElement>(null);
    const [animationStarted, setAnimationStarted] = useState(false);

    useEffect(() => {
        // Start animation sequence after a short delay
        const timer = setTimeout(() => {
            setAnimationStarted(true);
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        // Load particles.js from CDN if it's not already loaded
        if (typeof window.particlesJS === 'undefined') {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/particles.js/2.0.0/particles.min.js';
            script.async = true;
            script.onload = initializeParticles;
            document.body.appendChild(script);
        } else {
            initializeParticles();
        }

        return () => {
            // Clean up particles when component unmounts
            if (window.pJSDom && window.pJSDom.length > 0) {
                for (let i = 0; i < window.pJSDom.length; i++) {
                    const dom = window.pJSDom[i];
                    if (dom.pJS.canvas.el.id === 'breadcrumb-particles-canvas') {
                        window.pJSDom.splice(i, 1);
                        break;
                    }
                }
            }
        };
    }, []);

    const initializeParticles = () => {
        if (typeof window.particlesJS !== 'undefined' && particlesRef.current) {
            window.particlesJS('breadcrumb-particles', {
                "particles": {
                    "number": {
                        "value": 80,
                        "density": {
                            "enable": true,
                            "value_area": 800
                        }
                    },
                    "color": {
                        "value": "#3f51b5"  // Blue color
                    },
                    "shape": {
                        "type": "circle",
                        "stroke": {
                            "width": 0,
                            "color": "#000000"
                        }
                    },
                    "opacity": {
                        "value": 0.65,  // Increased opacity for better visibility
                        "random": false
                    },
                    "size": {
                        "value": 3,
                        "random": true
                    },
                    "line_linked": {
                        "enable": true,
                        "distance": 150,
                        "color": "#3f51b5",  // Blue color for lines
                        "opacity": 0.55,      // Increased opacity for lines
                        "width": 1
                    },
                    "move": {
                        "enable": true,
                        "speed": 2,
                        "direction": "none",
                        "random": true,
                        "straight": false,
                        "out_mode": "out",
                        "bounce": false
                    }
                },
                "interactivity": {
                    "detect_on": "canvas",
                    "events": {
                        "onhover": {
                            "enable": true,
                            "mode": "grab"
                        },
                        "onclick": {
                            "enable": true,
                            "mode": "push"
                        },
                        "resize": true
                    },
                    "modes": {
                        "grab": {
                            "distance": 200,
                            "line_linked": {
                                "opacity": 0.9  // More visible interaction
                            }
                        },
                        "push": {
                            "particles_nb": 3
                        }
                    }
                },
                "retina_detect": true
            });
        }
    };

    // Calculate animation delays
    const getTitleAnimationClass = () => {
        return animationStarted ? 'animate-title' : '';
    };

    const getItemAnimationClass = (index: number) => {
        return animationStarted ? `animate-item animate-delay-${index}` : '';
    };

    return (
        <div className="breadcrumb-wrapper">
            {/* Background image with overlay */}
            <div className="breadcrumb-bg" 
                 style={{ 
                     backgroundImage: 'url("/assets/img/BlueStone_min.jpg")',
                     position: 'absolute',
                     top: 0,
                     left: 0,
                     right: 0,
                     bottom: 0,
                     opacity: 0.15, // Very low opacity to make it barely visible
                     zIndex: 1
                 }}>
            </div>
            
            {/* Particles animation layer */}
            <div id="breadcrumb-particles" 
                 ref={particlesRef}
                 style={{
                     position: 'absolute',
                     top: 0,
                     left: 0,
                     right: 0,
                     bottom: 0,
                     zIndex: 2
                 }}>
            </div>
            
            {/* Content */}
            <div className="container breadcrumb-content-container" 
                 style={{
                     padding: 25, 
                     position: 'relative',
                     zIndex: 3,
                     pointerEvents: 'none' // Make container transparent to mouse events
                 }}>
                <div className="page-heading">
                    <h1 className={`breadcrumb-title ${getTitleAnimationClass()}`}>
                        {breadcrumbTitle}
                    </h1>
                    <ul className="breadcrumb-items">
                        {breadcrumbItems ? (
                            // Render using the provided breadcrumb array
                            breadcrumbItems.map((item: BreadcrumbItem, index: number) => (
                                <li key={index} className={getItemAnimationClass(index + 1)}>
                                    {index > 0 && (
                                        <i className="fas fa-chevron-right" style={{ margin: '0 8px' }} />
                                    )}
                                    {index === breadcrumbItems.length - 1 ? (
                                        <span className="current-item" style={{ pointerEvents: 'auto' }}>
                                            {t(item.label)}
                                        </span>
                                    ) : (
                                        <Link to={item.path} className="breadcrumb-link" style={{ pointerEvents: 'auto' }}>
                                            {t(item.label)}
                                        </Link>
                                    )}
                                </li>
                            ))
                        ) : (
                            // Fallback for backwards compatibility
                            <>
                                <li className={getItemAnimationClass(1)}>
                                    <Link to="/" className="breadcrumb-link" style={{ pointerEvents: 'auto' }}>
                                        {t("Home")}
                                    </Link>
                                </li>
                                <li className={getItemAnimationClass(1)}>
                                    <i className="fas fa-chevron-right" style={{ margin: '0 8px' }} />
                                </li>
                                <li className={getItemAnimationClass(2)}>
                                    <span className="current-item" style={{ pointerEvents: 'auto' }}>
                                        {t(breadcrumbTitle || '')}
                                    </span>
                                </li>
                            </>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}